import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import PackOpener from './components/PackOpener';
import DrawHistory from './components/DrawHistory';
import PapabiliList from './components/PapabiliList';
import ElectorsList from './components/ElectorsList';
import CardinalDetail from './components/CardinalDetail';
import ConclaveInfo from './components/ConclaveInfo';
import ConclavePrayer from './components/ConclavePrayer';
import HistoryCardinalate from './pages/HistoryCardinalate';
import CardinalRoles from './pages/CardinalRoles';
import ElectingPope from './pages/ElectingPope';
import MailForm from './pages/MailForm';
import FormulaireLong from './pages/FormulaireLong';
import { Cardinal, DrawHistory as DrawHistoryType } from './types';
import cardinauxData from './data/cardinaux_with_bios.json';

// Composant principal qui contient la logique de l'application
function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [cardinaux, setCardinaux] = useState<Cardinal[]>([]);
  const [drawHistory, setDrawHistory] = useState<DrawHistoryType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState<'home' | 'electors' | 'papabili'>('home');
  const [cardinalSelected, setCardinalSelected] = useState<boolean>(false);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);

  // Écouteur de défilement pour changer l'apparence du header
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 10); // Défini seuil à 10px
    };

    window.addEventListener('scroll', handleScroll);
    
    // Nettoyage de l'écouteur lors du démontage du composant
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    // Charger les données des cardinaux
    try {
      // Convertir les données JSON en objets Cardinal
      const loadedCardinaux: Cardinal[] = cardinauxData.map((cardinal: any) => ({
        url: cardinal.url || '',
        imageUrl: cardinal.imageUrl || null,
        coatOfArmsUrl: cardinal.coatOfArmsUrl || null,
        name: cardinal.name || 'Cardinal Inconnu',
        flagUrl: cardinal.flagUrl || null,
        elector: cardinal.elector || null,
        birthDate: cardinal.birthDate || null,
        creationDate: cardinal.creationDate || null,
        createdByFrancis: cardinal.createdByFrancis !== undefined ? cardinal.createdByFrancis : null,
        function: cardinal.function || null,
        country: cardinal.country || null,
        continent: cardinal.continent || null,
        title: cardinal.title || null,
        age: cardinal.age || null,
        biography: cardinal.biography || null,
        isPapabili: cardinal.isPapabili === true
      }));

      setCardinaux(loadedCardinaux);
      
      // Charger l'historique des tirages depuis le localStorage
      const savedHistory = localStorage.getItem('drawHistory');
      if (savedHistory) {
        try {
          const parsedHistory = JSON.parse(savedHistory);
          // Convertir les dates de string à Date
          const formattedHistory = parsedHistory.map((item: any) => ({
            ...item,
            date: new Date(item.date)
          }));
          setDrawHistory(formattedHistory);
        } catch (e) {
          console.error('Erreur lors du parsing de l\'historique:', e);
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Sauvegarder l'historique des tirages dans le localStorage
  useEffect(() => {
    if (drawHistory.length > 0) {
      localStorage.setItem('drawHistory', JSON.stringify(drawHistory));
    }
  }, [drawHistory]);

  const handleCardSelected = (cardinal: Cardinal) => {
    // Ajouter le tirage à l'historique
    const newDraw: DrawHistoryType = {
      date: new Date(),
      cardinal
    };
    
    setDrawHistory(prev => [newDraw, ...prev]);
    // Marquer qu'un cardinal a été sélectionné pour masquer la prière
    setCardinalSelected(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-900 to-gray-800">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-16 h-16 border-4 border-yellow-500 rounded-full border-t-transparent"
        />
      </div>
    );
  }

  // Style pour l'arrière-plan de la chapelle Sixtine
  const backgroundStyle = {
    backgroundImage: 'url("/sistine_chapel.jpg")',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundAttachment: 'fixed',
    position: 'relative' as 'relative'
  };

  // Style pour l'overlay semi-transparent
  const overlayStyle = {
    position: 'absolute' as 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(17, 24, 39, 0.5)', // bg-gray-900 avec 85% d'opacité
    zIndex: 0
  };

  // Style pour le contenu
  const contentStyle = {
    position: 'relative' as 'relative',
    zIndex: 1
  };

  // Filtrer les cardinaux électeurs pour la page des électeurs
  const electorCardinaux = cardinaux.filter(cardinal => cardinal.elector === 'E');

  // Vérifier si nous sommes sur une page de détail de cardinal
  const isCardinalDetailPage = location.pathname.includes('/cardinal/');

  return (
    <div className="min-h-screen text-white" style={backgroundStyle}>
      {/* Overlay semi-transparent */}
      <div style={overlayStyle}></div>
      
      {/* Contenu de l'application */}
      <div style={contentStyle}>
        {/* Header avec navigation */}
        <header className={`py-4 md:sticky md:top-0 md:z-50 transition-all duration-300 ${isScrolled ? 'md:bg-gradient-to-r md:from-gray-800 md:to-gray-900 md:shadow-md' : 'bg-transparent'}`}>
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center md:justify-between">
              <div className="flex-shrink-0 w-full text-center md:text-left md:w-auto mb-4 md:mb-0">
                <Link to="/" className="text-xl sm:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600 inline-block">
                  CONCLAVE
                </Link>
              </div>
              <nav className="flex flex-wrap justify-center space-x-2 sm:space-x-4 w-full md:w-auto pb-2 md:pb-0">
                <Link 
                  to="/"
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${location.pathname === '/' ? 'bg-red-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'}`}
                >
                  Mon Cardinal
                </Link>
                <Link 
                  to="/electors"
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${location.pathname === '/electors' ? 'bg-red-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'}`}
                >
                  Les Électeurs
                </Link>
                <Link 
                  to="/papabili"
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${location.pathname === '/papabili' ? 'bg-red-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'}`}
                >
                  Les Papabili
                </Link>
              </nav>
            </div>
          </div>
        </header>

        <main className="flex-grow container mx-auto px-4 py-4 sm:py-6 pt-14 sm:pt-6 md:pt-4">
          <Routes>
            <Route path="/" element={
              <>
                <motion.div 
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="text-center mb-8"
                >
                  <h1 className="text-5xl md:text-5xl font-bold text-white mb-4">
                    Habemus {' '}
                    <motion.span 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 1.2, duration: 4 }}
                      className="text-yellow-500"
                    >
                     Papam !
                    </motion.span>
                  </h1>
                  <p className="text-gray-300 mt-2 italic">
                  « Ce n’est pas vous qui m’avez choisi ; mais moi, je vous ai choisis et établis » (Jean 15, 16) 
                  </p>
                </motion.div>
                <PackOpener cardinaux={cardinaux} onCardSelected={handleCardSelected} />
                {!cardinalSelected && <ConclavePrayer />}
                <ConclaveInfo />
              </>
            } />
            <Route path="/electors" element={<ElectorsList cardinaux={electorCardinaux} />} />
            <Route path="/papabili" element={<PapabiliList cardinaux={cardinaux} />} />
            <Route path="/cardinal/:slug" element={<CardinalDetail cardinaux={cardinaux} />} />
            <Route path="/history-cardinalate" element={<HistoryCardinalate />} />
            <Route path="/cardinal-roles" element={<CardinalRoles />} />
            <Route path="/electing-pope" element={<ElectingPope />} />
            <Route path="/formulaire" element={<MailForm />} />
            <Route path="/email" element={<MailForm />} />
            <Route path="/formulaire2/:userId?" element={<FormulaireLong />} />
          </Routes>
        </main>

        <motion.footer
          initial={{ opacity: 0, y: 2 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mt-auto text-center text-gray-400 text-sm px-4 pb-8"
        >
          <p>{new Date().getFullYear()} Create Digital - Participez au conclave</p>
          <p className="mt-2">
            <a 
              href="https://www.vatican.va" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-yellow-500 hover:underline"
            >
              En savoir plus sur le site officiel du vatican
            </a>
          </p>
        </motion.footer>
      </div>
    </div>
  );
}



// Composant App qui enveloppe l'application avec le Router
function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
